import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Title from '../components/Title';
import SubmitButton from '../components/SubmitButton';
import NavigationStatus from '../components/NavigationStatus';
import useGtmEventTracking from '../components/useGtmEventTracking';
import { displayLoginName } from '../components/user/DisplayLoginName';
import UserContext from '../contexts/UserContext';

interface PackingOption {
  id: string;
  name: string;
  items: string[];
  rrp: number;
  price: number;
}

interface StorageOption {
  id: string;
  name: string;
  size: string;
  price: number;
}

const packingOptions: PackingOption[] = [
  {
    id: 'small',
    name: 'Small Pack',
    items: [
      '5× Large boxes',
      '5× Extra large boxes',
      '5× Bubble wrap',
      '3× Tape'
    ],
    rrp: 8800, // £88 in pence
    price: 8900 // £89 in pence
  },
  {
    id: 'medium',
    name: 'Medium Pack',
    items: [
      '10× Extra large boxes',
      '5× Large boxes',
      '8× Bubble wrap',
      '5× Tape'
    ],
    rrp: 16050, // £160.50 in pence
    price: 15900 // £159 in pence
  },
  {
    id: 'large',
    name: 'Large Pack',
    items: [
      '10× Extra large boxes',
      '15× Large boxes',
      '10× Medium boxes',
      '5× Small boxes',
      '10× Bubble wrap',
      '5× Tape'
    ],
    rrp: 26000, // £260 in pence
    price: 26900 // £269 in pence
  }
];

const storageOptions: StorageOption[] = [
  {
    id: 'mini',
    name: 'Mini Storage',
    size: 'up to 50sq ft',
    price: 9900 // £99.00/month in pence
  },
  {
    id: 'small',
    name: 'Small Storage',
    size: 'up to 100sq ft',
    price: 19900 // £199.00/month in pence
  },
  {
    id: 'standard',
    name: 'Standard Storage',
    size: 'up to 150sq ft',
    price: 29700 // £297.00/month in pence
  },
  {
    id: 'large',
    name: 'Large Storage',
    size: 'up to 200sq ft',
    price: 39600 // £396.00/month in pence
  }
];

function RemovalsExtrasComponent(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const gtmEventTracking = useGtmEventTracking();
  const { user } = useContext(UserContext);

  // Get data from location state
  const locationState = location.state as any;
  const selectedTier = locationState?.selectedTier;

  const [wantsPacking, setWantsPacking] = useState<boolean | null>(null);
  const [selectedPacking, setSelectedPacking] = useState<string>('');
  const [wantsStorage, setWantsStorage] = useState<boolean | null>(null);
  const [selectedStorage, setSelectedStorage] = useState<string>('');

  useEffect(() => {
    window.scrollTo(0, 0);
    gtmEventTracking({ event: 'booking_step_removals_extras' });
  }, [gtmEventTracking]);

  // Get suggested storage based on tier
  const getSuggestedStorage = React.useCallback(() => {
    if (!selectedTier) return 'standard';
    const tierMap: { [key: string]: string } = {
      'mini': 'mini',
      'small': 'small',
      'standard': 'standard',
      'large': 'large'
    };
    return tierMap[selectedTier.id] || 'standard';
  }, [selectedTier]);

  // Check if selected storage is smaller than recommended
  const getStorageWarning = React.useCallback(() => {
    if (!selectedStorage || !selectedTier) return null;
    
    const storageOrder = ['mini', 'small', 'standard', 'large'];
    const suggestedStorage = getSuggestedStorage();
    const selectedIndex = storageOrder.indexOf(selectedStorage);
    const suggestedIndex = storageOrder.indexOf(suggestedStorage);
    
    if (selectedIndex < suggestedIndex) {
      const suggestedOption = storageOptions.find(s => s.id === suggestedStorage);
      return {
        type: 'smaller',
        message: `Based on your ${selectedTier.name.toLowerCase()} move, we recommend ${suggestedOption?.name} (${suggestedOption?.size}) to ensure you have enough space for all your belongings.`
      };
    }
    
    return null;
  }, [selectedStorage, selectedTier, getSuggestedStorage]);

  // Pre-select packing based on tier
  useEffect(() => {
    if (selectedTier && wantsPacking === true && !selectedPacking) {
      const tierMap: { [key: string]: string } = {
        'mini': 'small',
        'small': 'small',
        'standard': 'medium',
        'large': 'large'
      };
      const suggestedPacking = tierMap[selectedTier.id] || 'medium';
      setSelectedPacking(suggestedPacking);
    }
  }, [wantsPacking, selectedTier, selectedPacking]);

  // Pre-select storage based on tier
  useEffect(() => {
    if (selectedTier && wantsStorage === true && !selectedStorage) {
      const suggestedStorage = getSuggestedStorage();
      setSelectedStorage(suggestedStorage);
    }
  }, [wantsStorage, selectedTier, selectedStorage, getSuggestedStorage]);

  const isFormValid = () => {
    const packingValid = wantsPacking === null ? false : 
                         wantsPacking === false ? true : 
                         selectedPacking !== '';
    const storageValid = wantsStorage === null ? false :
                        wantsStorage === false ? true :
                        selectedStorage !== '';
    return packingValid && storageValid;
  };

  const handleContinue = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
    }
    
    if (isFormValid()) {
      // Prepare extras data for backend (already in pence)
      let packingDetails = null;
      let storageDetails = null;

      if (wantsPacking && selectedPacking) {
        const packingOption = packingOptions.find(p => p.id === selectedPacking);
        if (packingOption) {
          packingDetails = packingOption;
        }
      }

      if (wantsStorage && selectedStorage) {
        const storageOption = storageOptions.find(s => s.id === selectedStorage);
        if (storageOption) {
          storageDetails = storageOption;
        }
      }

      // Navigate to collection/delivery address with all accumulated data
      history.push('/removals/collection_address', {
        ...locationState,
        boxes: [], // Removals don't use boxes
        wantsPacking,
        selectedPacking: packingDetails,
        wantsStorage,
        selectedStorage: storageDetails,
        removalService: true
      });
    }
  };

  if (!selectedTier) {
    history.replace('/removals');
    return <div>Redirecting...</div>;
  }

  return (
    <>
      <section className="login-status-bar">
        <div className="status-bar-content">
          <p className="grey">{displayLoginName(user)}</p>
        </div>
      </section>
      <NavigationStatus 
        step={3} 
        locationState={locationState}
      />

      <div className="removals-questions-container">
        <Title>Your move</Title>
        <p className="grey text-center mb-4">
          Add packing materials or storage to your {selectedTier.name.toLowerCase()} removal service.
        </p>

        <div className="form">
          <div id="removals-funnel-wrapper" className="sections questions mt-n3 mt-md-0">
            
            {/* Packing Materials Question */}
            <div className="open transparent dropdown mb-4">
              <div className="card">
                <div className="card-header dropdown-header">
                  <div className="header-title">Would you like packing materials?</div>
                </div>
                <div className="dropdown-body">
                  <div className="text-start ls-grey1 mb-3">
                    We recommend the {selectedTier.id === 'mini' || selectedTier.id === 'small' ? 'small' : selectedTier.id === 'standard' ? 'medium' : 'large'} pack for your {selectedTier.name.toLowerCase()} move.
                  </div>
                  <div className="dropdown-content">
                    <div 
                      className={`ls-radio-button ${wantsPacking === true ? 'selected' : ''}`}
                      onClick={() => setWantsPacking(true)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">Yes</div>
                    </div>
                    <div 
                      className={`ls-radio-button ${wantsPacking === false ? 'selected' : ''}`}
                      onClick={() => {
                        setWantsPacking(false);
                        setSelectedPacking('');
                      }}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">No</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Packing Options - Show if user wants packing */}
            {wantsPacking === true && (
              <div className="open transparent dropdown mb-4">
                <div className="card">
                  <div className="card-header dropdown-header">
                    <div className="header-title">Select your packing pack</div>
                  </div>
                  <div className="dropdown-body">
                    <div className="dropdown-content packing-options">
                      {packingOptions.map((option) => (
                        <div 
                          key={option.id}
                          className={`packing-card ${selectedPacking === option.id ? 'selected' : ''}`}
                          onClick={() => setSelectedPacking(option.id)}
                        >
                          <div className="packing-header">
                            <h4 className="ls-bold">{option.name}</h4>
                            <div className="packing-price">
                              <span className="rrp">£{(option.rrp / 100).toFixed(2).replace('.00', '')}</span>
                              <span className="price ls-bold">Pack: £{(option.price / 100).toFixed(2).replace('.00', '')}</span>
                            </div>
                          </div>
                          <div className="packing-items">
                            {option.items.map((item, idx) => (
                              <div key={idx} className="packing-item">• {item}</div>
                            ))}
                          </div>
                          {selectedPacking === option.id && (
                            <div className="selected-indicator">✓ Selected</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Storage Question */}
            <div className="open transparent dropdown mb-4">
              <div className="card">
                <div className="card-header dropdown-header">
                  <div className="header-title">Would you like your belongings stored?</div>
                </div>
                <div className="dropdown-body">
                  <div className="text-start ls-grey1 mb-3">
                    Need temporary storage during your move? We can help!
                  </div>
                  <div className="dropdown-content">
                    <div 
                      className={`ls-radio-button ${wantsStorage === true ? 'selected' : ''}`}
                      onClick={() => setWantsStorage(true)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">Yes</div>
                    </div>
                    <div 
                      className={`ls-radio-button ${wantsStorage === false ? 'selected' : ''}`}
                      onClick={() => {
                        setWantsStorage(false);
                        setSelectedStorage('');
                      }}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">No</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Storage Options - Show if user wants storage */}
            {wantsStorage === true && (
              <div className="open transparent dropdown mb-4">
                <div className="card">
                  <div className="card-header dropdown-header">
                    <div className="header-title">Select your storage size</div>
                  </div>
                  <div className="dropdown-body">
                    {getStorageWarning() && (
                      <div className="alert alert-warning mb-3" style={{
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px'
                      }}>
                        <span style={{ 
                          fontSize: '20px', 
                          lineHeight: '1',
                          flexShrink: 0 
                        }}>⚠️</span>
                        <div style={{ flex: 1 }}>
                          <strong style={{ display: 'block', marginBottom: '4px' }}>Storage size notice:</strong>
                          <span style={{ fontSize: '14px', color: '#856404' }}>
                            {getStorageWarning()?.message}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="dropdown-content storage-options">
                      {storageOptions.map((option) => (
                        <div 
                          key={option.id}
                          className={`storage-card ${selectedStorage === option.id ? 'selected' : ''}`}
                          onClick={() => setSelectedStorage(option.id)}
                        >
                          <div className="storage-header">
                            <h4 className="ls-bold">{option.name}</h4>
                            <div className="storage-size">{option.size}</div>
                          </div>
                          <div className="storage-price ls-bold">£{(option.price / 100).toFixed(2).replace('.00', '')}/month</div>
                          {selectedStorage === option.id && (
                            <div className="selected-indicator">✓ Selected</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div id="funnelFooter" className="mt-3">
            <div className="sticky-bar">
              <div className="sticky-bar-limit"></div>
              <div className="text-center sticky-bar-body">
                <SubmitButton 
                  type="button"
                  disabled={!isFormValid()}
                  onClick={handleContinue}
                >
                  Continue to Addresses
                </SubmitButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RemovalsExtrasComponent;
